// Citation formatting utilities for APA 7th, MLA 9th, Chicago 17th
// Works with paper metadata from Semantic Scholar / OpenAlex / bookmarks

function getAuthorNames(authors) {
  if (!authors || !authors.length) return [];
  return authors.map(a => {
    const name = a.name || a.display_name || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return { first: '', last: '' };
    const last = parts[parts.length - 1];
    const first = parts.slice(0, -1).join(' ');
    return { first, last, full: name };
  });
}

export function formatAPA(paper) {
  const authors = getAuthorNames(paper.authors);
  const year = paper.year || 'n.d.';
  const title = (paper.title || 'Untitled').replace(/\.$/, '');
  const venue = paper.venue || '';
  const doi = paper.externalIds?.DOI || (paper.url?.includes('doi.org') ? paper.url.replace('https://doi.org/', '') : '');

  // Authors: Last, F. I., Last, F. I., & Last, F. I.
  let authorStr = '';
  if (authors.length === 0) {
    authorStr = '';
  } else if (authors.length === 1) {
    const a = authors[0];
    const initials = a.first.split(/\s+/).map(n => n[0] + '.').join(' ');
    authorStr = `${a.last}, ${initials}`;
  } else if (authors.length <= 20) {
    authorStr = authors.map((a, i) => {
      const initials = a.first ? a.first.split(/\s+/).map(n => n[0] + '.').join(' ') : '';
      const prefix = i === authors.length - 1 ? '& ' : '';
      return `${i === 0 ? '' : prefix}${a.last}, ${initials}`;
    }).join(', ');
  } else {
    const first19 = authors.slice(0, 19).map((a, i) => {
      const initials = a.first ? a.first.split(/\s+/).map(n => n[0] + '.').join(' ') : '';
      return `${a.last}, ${initials}`;
    }).join(', ');
    const lastA = authors[authors.length - 1];
    const lastInitials = lastA.first ? lastA.first.split(/\s+/).map(n => n[0] + '.').join(' ') : '';
    authorStr = `${first19}, ... ${lastA.last}, ${lastInitials}`;
  }

  let citation = `${authorStr} (${year}). ${title}.`;
  if (venue) citation += ` *${venue}*.`;
  if (doi) citation += ` https://doi.org/${doi}`;

  return citation.trim();
}

export function formatMLA(paper) {
  const authors = getAuthorNames(paper.authors);
  const year = paper.year || '';
  const title = (paper.title || 'Untitled').replace(/\.$/, '');
  const venue = paper.venue || '';
  const doi = paper.externalIds?.DOI || (paper.url?.includes('doi.org') ? paper.url.replace('https://doi.org/', '') : '');

  // Authors: Last, First, and First Last.
  let authorStr = '';
  if (authors.length === 1) {
    authorStr = `${authors[0].last}, ${authors[0].first}`;
  } else if (authors.length === 2) {
    authorStr = `${authors[0].last}, ${authors[0].first}, and ${authors[1].first} ${authors[1].last}`;
  } else if (authors.length >= 3) {
    authorStr = `${authors[0].last}, ${authors[0].first}, et al.`;
  }

  let citation = `${authorStr}. "${title}."`;
  if (venue) citation += ` *${venue}*,`;
  if (year) citation += ` ${year}`;
  if (doi) citation += `, https://doi.org/${doi}`;
  citation += '.';

  return citation.trim();
}

export function formatChicago(paper) {
  const authors = getAuthorNames(paper.authors);
  const year = paper.year || '';
  const title = (paper.title || 'Untitled').replace(/\.$/, '');
  const venue = paper.venue || '';
  const doi = paper.externalIds?.DOI || (paper.url?.includes('doi.org') ? paper.url.replace('https://doi.org/', '') : '');

  // Authors: Last, First, First Last, and First Last.
  let authorStr = '';
  if (authors.length === 1) {
    authorStr = `${authors[0].last}, ${authors[0].first}`;
  } else if (authors.length === 2) {
    authorStr = `${authors[0].last}, ${authors[0].first}, and ${authors[1].first} ${authors[1].last}`;
  } else if (authors.length === 3) {
    authorStr = `${authors[0].last}, ${authors[0].first}, ${authors[1].first} ${authors[1].last}, and ${authors[2].first} ${authors[2].last}`;
  } else if (authors.length >= 4) {
    authorStr = `${authors[0].last}, ${authors[0].first}, ${authors[1].first} ${authors[1].last}, ${authors[2].first} ${authors[2].last}, et al.`;
  }

  let citation = `${authorStr}. "${title}."`;
  if (venue) citation += ` *${venue}*`;
  if (year) citation += ` (${year})`;
  if (doi) citation += `. https://doi.org/${doi}`;
  citation += '.';

  return citation.trim();
}

export function generateBibliography(papers, style = 'apa') {
  const formatter = style === 'mla' ? formatMLA : style === 'chicago' ? formatChicago : formatAPA;
  return papers
    .map(p => formatter(p))
    .sort((a, b) => a.localeCompare(b))
    .join('\n\n');
}

export const CITATION_STYLES = [
  { id: 'apa', label: 'APA 7th', description: 'American Psychological Association' },
  { id: 'mla', label: 'MLA 9th', description: 'Modern Language Association' },
  { id: 'chicago', label: 'Chicago 17th', description: 'Chicago Manual of Style' },
];
